import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export const useGalleryLikes = (imageIds: string[]) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch which images the user has liked
  const { data: userLikes = [] } = useQuery({
    queryKey: ["gallery-user-likes", user?.id, imageIds],
    queryFn: async () => {
      if (!user || imageIds.length === 0) return [];

      const { data, error } = await supabase
        .from("gallery_likes")
        .select("image_id")
        .eq("user_id", user.id)
        .in("image_id", imageIds);

      if (error) throw error;
      return data.map((l) => l.image_id);
    },
    enabled: !!user && imageIds.length > 0,
  });

  const likeMutation = useMutation({
    mutationFn: async (imageId: string) => {
      const { data, error } = await supabase.rpc("like_gallery_image", {
        p_image_id: imageId,
      });
      if (error) throw error;
      return { imageId, success: data };
    },
    onSuccess: ({ imageId }) => {
      queryClient.invalidateQueries({ queryKey: ["public-gallery"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-user-likes"] });
    },
    onError: () => {
      toast.error("Erreur lors du like");
    },
  });

  const unlikeMutation = useMutation({
    mutationFn: async (imageId: string) => {
      const { data, error } = await supabase.rpc("unlike_gallery_image", {
        p_image_id: imageId,
      });
      if (error) throw error;
      return { imageId, success: data };
    },
    onSuccess: ({ imageId }) => {
      queryClient.invalidateQueries({ queryKey: ["public-gallery"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-user-likes"] });
    },
    onError: () => {
      toast.error("Erreur lors du unlike");
    },
  });

  const toggleLike = useCallback(
    (imageId: string) => {
      if (!user) {
        toast.error("Connectez-vous pour liker");
        return;
      }

      const isLiked = userLikes.includes(imageId);
      if (isLiked) {
        unlikeMutation.mutate(imageId);
      } else {
        likeMutation.mutate(imageId);
      }
    },
    [user, userLikes, likeMutation, unlikeMutation]
  );

  const isLiked = useCallback(
    (imageId: string) => userLikes.includes(imageId),
    [userLikes]
  );

  return {
    toggleLike,
    isLiked,
    isLoading: likeMutation.isPending || unlikeMutation.isPending,
  };
};
